import { sendGAEvent } from '../utils';

export enum DepositEventsEnum {
  DEPOSIT_SUBMIT = 'deposit_submit',
  DEPOSIT_APPEARENCE = 'deposit_appearance',
  DEPOSIT_BONUS_CLICK = 'deposit_bonus_click',
  DEPOSIT_PROMO_CODE_SUBMIT = 'deposit_promo_code_submit',
  DEPOSIT_PAYMENT_METHOD_CLICK = 'deposit_payment_method_click',
  DEPOSIT_CLOSE = 'deposit_close',
  DEPOSIT_PAYMENT_METHODS_SHOW_MORE_CLICK = 'deposit_payment_methods_show_more_click',
  DEPOSIT_PAYMENT_METHOD_CRASHED = 'deposit_payment_method_crashed',
  DEPOSIT_PAYMENT_METHODS_FAILED_LOADING = 'deposit_payment_methods_failed_loading',
  DEPOSIT_PAYMENT_METHODS_SUBMIT_ERROR = 'deposit_payment_methods_submit_error',
  DEPOSIT_NO_METHODS_APPEARENCE = 'deposit_no_methods_appearance',
  DEPOSIT_PAYMENTS_API_INIT_FAILED = 'deposit_payments_api_init_failed',
  CURRENCY_RATES_FAILED = 'currency_rates_failed',
}

export interface IDepositAppearenceData {
  dep_modal_type: 'slots' | 'slots-vip';
  amount: string | number | null;
  currency: string;
}

export interface IDepositBonusClick {
  dep_modal_type: 'slots' | 'slots-vip';
  type: 'on' | 'off' | 'hint_open' | 'hint_close';
  id: string | number;
}

interface IDepositPromoCodeSubmit {
  code?: string;
  type: 'activate' | 'deactivate';
}
interface IDepositPaymentMethodShowMoreClick {
  type: 'more' | 'less';
}

interface IDepositPaymentMethodClick {
  dep_modal_type: 'slots' | 'slots-vip';
  payment_brand: string;
  payment_provider: string;
  payment_method_id: string;
  payment_type: string;
}

interface IPaymentMethodCrashed {
  payment_method_id: string;
  field_title: string;
  field_name: string;
  field_type: string;
  error?: string;
}

interface IPaymentMethodsSubmitError {
  submit_error: string;
}

interface IDepositPaymentMethodsFailedLoading {
  error?: string;
  value?: unknown;
}

interface IDepositSubmit {
  amount: number | string;
  currency: string;
  button_text: 'deposit' | 'copy_address';
  status: 'success' | 'failed';
  request_data: string;
  error_data: string;
  payment_brand?: string;
  payment_provider?: string;
  payment_method_id?: string;
  payment_type?: string;
  dep_modal_type: 'slots' | 'slots-vip';
}

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when a deposit modal is shown.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_appearance` and the category is `deposit`.
 * The data object contains the deposit modal type, amount, and currency.
 */
export const sendDepositAppearanceEvent = (data: IDepositAppearenceData) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_APPEARENCE, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on some bonus card/hint
 * on step 1 inside deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_bonus_click` and the category is `deposit`.
 * The data object contains the deposit modal type, bonus type (`on`, `off`, or `hint`), and bonus id.
 */
export const sendDepositBonusClickEvent = (data: IDepositBonusClick) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_BONUS_CLICK, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on promocode
 * activate button inside deposit modal, on promocode tab.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_promo_code_activate` and the category is `deposit`.
 * The data object contains the deposit modal type, promocode, status, and optional error data.
 */
export const sendDepositPromoCodeSubmitEvent = (
  data: IDepositPromoCodeSubmit
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PROMO_CODE_SUBMIT, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 * Sends a Google Analytics event when the user clicks the "Show more" or "Show less"
 * button in the payment methods list inside the deposit modal.
 * @param data - Additional event data.
 * @param data.type - Indicates whether the user expanded or collapsed the list.
 * Use `'more'` when showing additional payment methods,
 * and `'less'` when collapsing them back.
 * @description This function tracks user interaction with the expandable payment methods section
 * (e.g., clicking "Show 10 more" or "Show less"). It uses the `sendGAEvent` utility
 * with the event name `deposit_payment_methods_show_more_click` and category `deposit`.
 */
export const sendDepositPaymentMethodShowMoreClickEvent = (
  data: IDepositPaymentMethodShowMoreClick
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHODS_SHOW_MORE_CLICK, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on some payment
 * method on step 2 inside deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_payment_method_click` and the category is `deposit`.
 * The data object contains the deposit modal type and payment method.
 */
export const sendDepositPaymentMethodClickEvent = (
  data: IDepositPaymentMethodClick
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHOD_CLICK, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user close the deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_close` and the category is `deposit`.
 * The data object contains the deposit modal type.
 */
export const sendDepositCloseEvent = () => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_CLOSE, {
    event_category: 'deposit',
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when a payment method crashed the deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_payment_method_crashed` and the category is `deposit`.
 * The data object contains the deposit modal type.
 */
export const sendDepositPaymentMethodCrashedEvent = (
  data: IPaymentMethodCrashed
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHOD_CRASHED, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @description This function sends a Google Analytics event when a payment method not loaded in deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_payment_methods_failed_loading` and the category is `deposit`.
 * The data object contains the deposit modal type.
 */
export const sendDepositPaymentMethodsFailedLoading = (
  data?: IDepositPaymentMethodsFailedLoading
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHODS_FAILED_LOADING, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when a payment method submit returns an error.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_payment_methods_submit_error` and the category is `deposit`.
 * The data object contains the deposit modal type.
 */
export const sendDepositPaymentSubmitError = (
  data: IPaymentMethodsSubmitError
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHODS_SUBMIT_ERROR, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on deposit button
 * inside deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_submit` and the category is `deposit`.
 * The data object contains the deposit modal type, amount, currency, button text, request data, error data, and payment method.
 */
export const sendDepositSubmitEvent = (data: IDepositSubmit) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_SUBMIT, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @description This function sends a Google Analytics event when payment methods do not appear.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_no_methods_apprearance` and the category is `deposit`.
 */
export const sendDepositNoMethodsAppearence = () => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_NO_METHODS_APPEARENCE, {
    event_category: 'deposit',
  });
};

interface IDepositPaymentsAPIInitFailed {
  error: string;
}

/**
 * @param data - The data to be sent with the event.
 * @description Sends a Google Analytics event when PaymentsAPI initialization fails —
 * either due to script load timeout or config() rejection.
 * The event name is `deposit_payments_api_init_failed` and the category is `deposit`.
 */
export const sendDepositPaymentsAPIInitFailed = (
  data: IDepositPaymentsAPIInitFailed
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENTS_API_INIT_FAILED, {
    event_category: 'deposit',
    ...data,
  });
};

interface ICurrencyRatesFailed {
  currency: string;
}

/** Sent when cashout limit rates fail to load and the fallback table is used. */
export const sendCurrencyRatesFailed = (data: ICurrencyRatesFailed) => {
  sendGAEvent(DepositEventsEnum.CURRENCY_RATES_FAILED, {
    event_category: 'cashout',
    ...data,
  });
};
